import { useAuth } from 'contexts/auth';
import AccountSettings from 'pages/AccountSettings';
import AssignmentFeedbackPage from 'pages/AssignmentFeedback';
import AssignmentsPage from 'pages/Assignments';
import ComponentDemoPage from 'pages/ComponentDemo';
import CourseDashboardPage from 'pages/CourseDashboard';
import CourseSettings from 'pages/CourseSettings';
import CourseSetUpPage from 'pages/CourseSetup';
import CreateAssignmentPage from 'pages/CreateAssignment';
import CreateRubricPage from 'pages/CreateRubric';
import EditAssignmentPage from 'pages/EditAssignment';
import EditRubricPage from 'pages/EditRubric';
import Error404 from 'pages/Error/404';
import FederatedLoginPage from 'pages/FederatedLoginPage';
import ForgotPassword from 'pages/ForgotPassword';
import GradingPage from 'pages/Grading';
import LTIIncompletePage from 'pages/LTI/Incomplete';
import LoginPage from 'pages/Login';
import ProfilePage from 'pages/Profile';
import ProfileSetUpPage from 'pages/ProfileSetUp';
import RecoverPasswordPage from 'pages/RecoverPassword';
import RubricsPage from 'pages/Rubrics';
import RubricsGetStartedPage from 'pages/RubricsGetStarted';
import SignUpPage from 'pages/SignUp';
import StripePage from 'pages/Stripe';
import SubmissionsPage from 'pages/Submissions';
import SurveyPage from 'pages/Survey';
import Teams from 'pages/Teams';
import ViewAssignmentPage from 'pages/ViewAssignment';
import ViewRubricPage from 'pages/ViewRubric';
import ViewSubmissionPage from 'pages/ViewSubmission';
import { Route, Switch } from 'react-router-dom';
import { Loading } from 'shell/components';

const GrantPermission = ({ exact, path, component, roles, auth, user, userRole }) => {
    const exceptions = ['/components', '/accepted-invitation', '/lti-admin', '/lti-incomplete', '/lti-launch'];

    if (exceptions.includes(path)) {
        return <Route exact={exact} path={path} component={component} />;
    }

    if (auth && auth.role !== 'admin' && !auth.owner_account_id && user && !user.profile_set_up) {
        if (!user.cognito_user_id?.includes('google')) {
            return <Route exact={exact} path={path} component={AccountSettings} />;
        } else {
            return <Route exact={exact} path={path} component={ProfileSetUpPage} />;
        }
    } else if (auth && auth.role !== 'admin' && !auth.owner_account_id && !path === '/rubrics') {
        return <Route exact={exact} path={path} component={CourseDashboardPage} />;
    } else if (roles.length === 0 || roles?.includes(userRole)) {
        return <Route exact={exact} path={path} component={component} />;
    } else {
        return <Route component={Error404} />;
    }
};

const PasswordRoleBasedRouting = () => {
    const { auth, currentUser } = useAuth();

    const getDefaultComponent = (auth) => {
        if (!auth) return LoginPage;
        return CourseDashboardPage;
    };

    const noAuthRoutes = [
        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },
        { path: '/', component: LoginPage, roles: [], exact: true },
        { path: '/login', component: LoginPage, roles: [], exact: true },
        { path: '/sign-up', component: SignUpPage, roles: [], exact: true },
        { path: '/forgot-password', component: ForgotPassword, roles: [], exact: true },
        { path: '/recoverpassword', component: RecoverPasswordPage, roles: [], exact: true },
        { path: '/federated-login', component: FederatedLoginPage, roles: [], exact: true },
    ];

    const routes = [
        { path: '/stripe', component: StripePage, roles: [], exact: true }, // used for stripe debug
        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },
        { path: '/', component: getDefaultComponent(auth), roles: [], exact: true },
        { path: '/accepted-invitation', component: AccountSettings, roles: [], exact: true },
        { path: '/account-settings', component: AccountSettings, roles: [], exact: true },
        { path: '/lti-incomplete', component: LTIIncompletePage, roles: [], exact: true },
        { path: '/profile-setup', component: ProfileSetUpPage, roles: [], exact: true },

        { path: '/rubrics', component: RubricsPage, roles: [], exact: true },
        {
            path: '/rubrics/get-started',
            component: RubricsGetStartedPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/new',
            component: CreateRubricPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/:id',
            component: ViewRubricPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/:id/edit',
            component: EditRubricPage,
            roles: [],
            exact: true,
        },

        { path: '/assignments', component: AssignmentsPage, roles: ['instructor', 'student'], exact: true },
        { path: '/assignments/new', component: CreateAssignmentPage, roles: ['instructor'], exact: true },
        { path: '/assignments/:id', component: ViewAssignmentPage, roles: ['instructor', 'student'], exact: true },
        { path: '/assignments/:id/edit', component: EditAssignmentPage, roles: ['instructor'], exact: true },
        {
            path: '/assignments/:id/feedback',
            component: AssignmentFeedbackPage,
            roles: ['instructor'],
            exact: true,
        },
        {
            path: '/assignments/:id/feedback/:feedbackId',
            component: AssignmentFeedbackPage,
            roles: ['instructor', 'student'],
            exact: true,
        },
        { path: '/assignments/:id/grading', component: GradingPage, roles: ['instructor'], exact: true },
        { path: '/assignments/:id/grading/:submissionId', component: GradingPage, roles: ['instructor'], exact: true },
        { path: '/assignments/:id/submissions', component: SubmissionsPage, roles: ['instructor'], exact: true },
        {
            path: '/assignments/:id/submissions/:submissionId',
            component: ViewSubmissionPage,
            roles: ['instructor'],
            exact: true,
        },

        { path: '/profile', component: ProfilePage, roles: [], exact: true },
        { path: '/profile/:tab', component: ProfilePage, roles: [], exact: true },

        { path: '/course-set-up', component: CourseSetUpPage, roles: [], exact: true },
        { path: '/course-settings', component: CourseSettings, roles: ['instructor'], exact: true },
        { path: '/people', component: Teams, roles: ['instructor'], exact: true },
        { path: '/people/:tab', component: Teams, roles: ['instructor'], exact: true },

        {
            path: '/survey',
            component: SurveyPage,
            roles: ['student'],
            exact: true,
        },
    ];

    return auth === undefined ? (
        <Loading />
    ) : (
        <Switch>
            {((auth ? routes : noAuthRoutes) || []).map((route) => {
                return (
                    <GrantPermission
                        key={route.path}
                        exact={route.exact}
                        path={route.path}
                        component={route.component}
                        roles={route.roles}
                        auth={auth}
                        user={currentUser}
                        userRole={auth?.role}
                    />
                );
            })}
            <Route component={Error404} />
        </Switch>
    );
};

export default PasswordRoleBasedRouting;
